/*
 * Lets every open tab agree on which gifts have already been taken.
 *
 * Goalify records a taken gift in sessionStorage, which belongs to one tab,
 * while the cart it was added to belongs to all of them. A shopper who opens a
 * second tab — to compare a garment, as people do — is answering two pickers
 * that cannot see each other. Both spend the same gift, and once the app has no
 * goal left to attribute, it adds the product as an ordinary line carrying none
 * of its own markings. Its discount cannot recognise that line, so the shopper
 * is charged full price for something chosen from a dialog headed "Pick your
 * gift", and the total moves with no visible cause.
 *
 * Sharing the record is what stops that: told what the other tab has taken, the
 * app offers the gift that is genuinely still owing, or nothing at all.
 *
 * Only what was TAKEN is shared. Turning a gift down is a decision about the
 * dialog in front of you, not a fact about the cart, and carrying it across
 * would silently deny a gift the shopper is still owed in the other tab.
 *
 * The cart decides what stays true. A record kept after its gift has gone from
 * the cart would go on suppressing an offer the shopper has earned back, so the
 * shared copy is reconciled against the cart's own gift lines, each of which
 * names the goal it was granted for.
 */

if (!window.__umiGiftClaimsSync) {
  window.__umiGiftClaimsSync = true;

  (() => {
    const SHARED = "umi_gift_claims";
    const TAKEN = /^offer_(.+)_goal_(\d+)_free_gift_selected(_id)?$/;
    const POPUP = ".n99gb-fgPopup";
    const REEVALUATE = "SCE:update";

    const goalOf = (key) => (key.match(TAKEN) || [])[2];

    const read = () => {
      try {
        return JSON.parse(localStorage.getItem(SHARED)) || {};
      } catch {
        return {};
      }
    };

    const write = (claims) => {
      try {
        localStorage.setItem(SHARED, JSON.stringify(claims));
      } catch {
        // Private browsing refuses the write. Nothing here is required for a
        // gift to be granted, so a tab that cannot share simply behaves as it
        // did before.
      }
    };

    /* Which goals the cart can still vouch for. */
    const goalsInCart = async () => {
      const url = window.routes?.cart_url;
      if (!url) return null;
      const cart = await fetch(`${url}.js`)
        .then((response) => response.json())
        .catch(() => null);
      if (!cart) return null;

      return new Set(
        cart.items
          .filter((item) => item.properties?._is_n99gb_free_gift)
          .map((item) => String(item.properties._n99gb_goal_index)),
      );
    };

    /* This tab's own record, offered to the others. */
    const publish = (live) => {
      const claims = read();
      let changed = false;

      Object.keys(sessionStorage)
        .filter((key) => TAKEN.test(key))
        .forEach((key) => {
          const value = sessionStorage.getItem(key);
          if (claims[key] === value) return;
          claims[key] = value;
          changed = true;
        });

      // Drop anything the cart no longer shows, so a gift given back can be
      // offered again.
      if (live) {
        Object.keys(claims).forEach((key) => {
          const goal = goalOf(key);
          if (goal && !live.has(goal)) {
            delete claims[key];
            changed = true;
          }
        });
      }

      if (changed) write(claims);
    };

    /* What the others have taken, adopted here. Returns whether anything new. */
    const adopt = (live) => {
      const claims = read();
      let gained = false;

      Object.entries(claims).forEach(([key, value]) => {
        const goal = goalOf(key);
        if (goal && live && !live.has(goal)) return;
        if (sessionStorage.getItem(key) === value) return;
        sessionStorage.setItem(key, value);
        gained = true;
      });

      return gained;
    };

    /*
     * A dialog already on screen was drawn before the news arrived and may be
     * offering a gift another tab has since taken. Acting on it is what produces
     * the charged line, so it is dismissed and the app asked to draw what is
     * actually owing — which is often nothing.
     *
     * Dismissed through its own control, never by taking the element out of the
     * page. The app keeps its own note of whether a dialog is up, and that note
     * is only cleared by its dismissal running; a dialog removed behind its back
     * leaves the note set, and nothing it is asked for afterwards is ever drawn.
     * Only a fresh page recovers, which for a shopper mid-look means the gift is
     * simply gone.
     */
    const dismiss = () => {
      const popup = document.querySelector(POPUP);
      if (!popup) return true;

      const closer = Array.prototype.find.call(
        popup.querySelectorAll("button, span, div, a"),
        (node) => {
          const label = (node.textContent || "").trim();
          return (label === "×" || label === "✕") && node.offsetParent !== null;
        },
      );
      if (!closer) return false;

      closer.click();
      return true;
    };

    const redraw = () => {
      // Leave a dialog we cannot dismiss properly standing: a stale offer the
      // shopper might take is better than a tab that can never show one again.
      if (!dismiss()) return;
      window.dispatchEvent(new CustomEvent(REEVALUATE, { detail: {} }));
      document.dispatchEvent(new CustomEvent(REEVALUATE, { detail: {} }));
    };

    const reconcile = async ({ withRedraw = false } = {}) => {
      const live = await goalsInCart();
      const gained = adopt(live);
      publish(live);
      if (gained && withRedraw) redraw();
    };

    const start = () => {
      // Absent wherever the app is not loaded, and absent entirely if it is
      // removed — either way there is nothing to keep in step.
      if (!window.N99GB_goalsChunk1) return;

      reconcile();

      // Another tab took a gift.
      window.addEventListener("storage", (event) => {
        if (event.key !== SHARED) return;
        reconcile({ withRedraw: true });
      });

      /*
       * This tab took one. The record is written while the dialog is closing,
       * so the read is deferred past that turn rather than racing it.
       */
      new MutationObserver((mutations) => {
        const closed = mutations.some((mutation) =>
          Array.prototype.some.call(
            mutation.removedNodes,
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.(POPUP) || node.querySelector?.(POPUP)),
          ),
        );
        if (closed) setTimeout(() => reconcile(), 1200);
      }).observe(document.body, { childList: true, subtree: true });
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
  })();
}
